// src/pages/CartPage.js
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Trash2, Minus, Plus } from "lucide-react";

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart } = useContext(CartContext);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [checkoutIssues, setCheckoutIssues] = useState([]);
const [showModal, setShowModal] = useState(false);


  useEffect(() => {
    setTotal(cart.reduce((sum, item) => sum + item.subtotal, 0));
  }, [cart]);

const handleCheckout = async () => {
  try {
    const sessionID = localStorage.getItem("sessionID");
    const { data } = await axios.get(`${API_BASE_URL}/cart/validate`, {
      headers: { "Session-ID": sessionID }
    });

    if (data.valid) {
      navigate("/checkout");
    } else {
      console.log("Validation issues:", data.issues); // debug
      setCheckoutIssues(data.issues || []);
      setShowModal(true); // show animated modal
    }
  } catch (err) {
    console.error("Validation error:", err);
    setCheckoutIssues([{ reason: "Could not validate cart. Please try again." }]);
    setShowModal(true);
  }
};



  return (
    <>
      <div className="p-4 sm:p-10 max-w-6xl mx-auto bg-slate-50/50 rounded-[2rem] border border-slate-100 shadow-sm mt-20 mb-16 font-[Poppins]">
        <h1 className="text-2xl sm:text-3xl font-black mb-8 flex items-center gap-2.5 text-green-800 font-[Montserrat]">
          <ShoppingBag size={28} className="text-green-700" /> Your Cart
        </h1>

        {/* Empty cart state */}
        {cart.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 bg-white rounded-3xl border border-slate-100 p-8 shadow-sm"
          >
            <img
              src="/images/empty-cart.png"
              alt="Empty cart"
              className="w-36 mx-auto opacity-70 filter brightness-95"
            />
            <p className="mt-6 text-slate-600 font-semibold text-base sm:text-lg">
              Your cart is empty — time to fill it with freshness 🌱
            </p>
            <Link
              to="/"
              className="inline-block mt-6 px-8 py-3 bg-green-700 hover:bg-green-800 text-white font-bold rounded-full shadow-md transition-all"
            >
              Start Shopping
            </Link>
          </motion.div>
        ) : (
          <div className="cart-container">
            {/* Left Column: Cart items */}
            <div className="space-y-4">
              <AnimatePresence>
                {cart.map((item) => (
                  <motion.div
                    key={item.productId._id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.3 }}
                    className="cart-item flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4"
                  >
                    {/* Image */}
                    <div className="cart-item-img-wrapper">
                      <Link to={`/product/${item.productId._id}`}>
                        <motion.img
                          whileHover={{ scale: 1.03 }}
                          src={item.productImage}
                          alt={item.name}
                          className="w-24 h-24 sm:w-28 sm:h-28 object-contain p-2"
                        />
                      </Link>
                    </div>

                    {/* Details */}
                    <div className="flex-1 space-y-1 w-full">
                      <span className="text-[10px] font-black uppercase text-amber-600 tracking-wider font-[Montserrat]">
                        {item.productId.category || "Produce"}
                      </span>
                      <h2 className="font-black text-slate-800 text-base sm:text-lg font-[Montserrat] line-clamp-1">
                        {item.name}
                      </h2>
                      <p className="text-xs text-slate-400 font-medium">
                        ${Number(item.productId.price).toFixed(2)} each
                      </p>
                      <p className="text-sm text-green-700 font-extrabold pt-1">
                        Subtotal: ${Number(item.subtotal).toFixed(2)}
                      </p>
                    </div>

                    {/* Quantity + remove */}
                    <div className="flex sm:flex-col items-center justify-between sm:justify-center gap-4 w-full sm:w-auto pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                      <div className="qty-adjuster">
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          className="qty-btn"
                          onClick={() =>
                            updateQuantity(item.productId._id, item.quantity - 1)
                          }
                          disabled={item.quantity === 1}
                        >
                          <Minus size={14} />
                        </motion.button>
                        
                        <span className="qty-val">
                          {item.quantity}
                        </span>

                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          className="qty-btn"
                          onClick={() =>
                            item.quantity < item.productId.stock &&
                            updateQuantity(item.productId._id, item.quantity + 1)
                          }
                          disabled={item.quantity >= item.productId.stock}
                        >
                          <Plus size={14} />
                        </motion.button>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        onClick={() => removeFromCart(item.productId._id)}
                        className="flex items-center gap-1 text-red-500 hover:text-red-600 text-xs font-semibold p-1 transition-colors"
                      >
                        <Trash2 size={14} /> Remove
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Continue Shopping button */}
              <div className="pt-4">
                <Link
                  to="/"
                  className="text-green-700 hover:text-green-800 font-bold hover:underline flex items-center gap-1.5 text-sm"
                >
                  <i className="bx bx-left-arrow-alt text-lg"></i> Continue Shopping
                </Link>
              </div>
            </div>

            {/* Right Column: Checkout Summary block */}
            <div className="summary-card flex flex-col gap-4 sticky top-24">
              <h3 className="text-base sm:text-lg font-black text-slate-800 border-b pb-3.5 font-[Montserrat]">
                Order Summary
              </h3>
              
              <div className="flex justify-between text-xs sm:text-sm text-slate-600 font-medium">
                <span>Items Subtotal</span>
                <span className="font-extrabold text-slate-800">${Number(total).toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-xs sm:text-sm text-slate-600 font-medium pb-2">
                <span>Delivery Fee</span>
                <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-[11px]">
                  Estimated at Checkout
                </span>
              </div>

              <div className="border-t border-slate-100 pt-4 flex justify-between text-sm sm:text-base font-black text-slate-900 font-[Montserrat]">
                <span>Estimated Total</span>
                <span className="text-green-700 text-base sm:text-lg font-black">
                  ${Number(total).toFixed(2)}
                </span>
              </div>

              <Link
                onClick={(e) => {
                  e.preventDefault();
                  handleCheckout();
                }}
                to="#"
                className="w-full text-center py-3 bg-gradient-to-r from-green-700 to-green-600 text-white rounded-full font-bold shadow-[0_8px_20px_-4px_rgba(21,128,61,0.2)] hover:from-green-800 hover:to-green-700 hover:shadow-[0_12px_24px_-4px_rgba(21,128,61,0.3)] transition-all duration-300 mt-4 block text-sm"
              >
                Proceed to Checkout
              </Link>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/60 z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative border border-slate-100 font-[Poppins]"
            >
              <div className="flex items-center gap-3 border-b pb-4 mb-4">
                <div className="p-2 bg-red-50 rounded-2xl text-red-600">
                  <i className="bx bx-error-circle text-2xl" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-black text-slate-800 font-[Montserrat]">
                    Stock Issues Detected
                  </h2>
                  <p className="text-xs text-slate-400 font-medium">Please review availability before checking out</p>
                </div>
              </div>

              <ul className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                {checkoutIssues.map((issue, idx) => (
                  <li
                    key={idx}
                    className="p-3.5 rounded-2xl bg-red-50/50 border border-red-100 text-xs sm:text-sm text-red-800 flex flex-col gap-1"
                  >
                    <span className="font-black font-[Montserrat]">{issue.name || "Item"}</span>
                    <span className="font-medium text-slate-600">
                      {issue.reason}
                      {issue.available != null && (
                        <span className="font-bold text-red-700">
                          {" "}— {issue.available} remaining
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs sm:text-sm transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    navigate("/");
                  }}
                  className="px-5 py-2.5 rounded-full bg-green-700 hover:bg-green-800 text-white font-bold text-xs sm:text-sm shadow-sm transition-all"
                >
                  Continue Shopping
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}
